/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback, useRef} from 'react';
import {useMediaQuery} from 'react-responsive';
import {
  Button,
  Fab,
  CircularProgress,
  TextField,
  IconButton,
  InputAdornment,
  Divider,
  Drawer,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Visibility,
  VisibilityOff,
  ArrowForward,
  ArrowBack,
  CheckCircleOutline,
  Android,
  Apple,
} from '@mui/icons-material';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  updateProfile,
  signOut,
} from 'firebase/auth';
import styled, {keyframes} from 'styled-components';
import {Link} from 'react-router-dom';
import Tab from '../../Components/tab';
import Footer from '../../Components/Footer';
import logo from '../../Assets/logo.png';
import StepPessoal from './StepPessoal';
import StepCnh from './StepCnh';
import StepIntencao from './StepIntencao';
import StepVeiculo from './StepVeiculo';
import StepLocation from './StepLocation';
import StepPagamento from './StepPagamento';
import StepNav from './StepNav';

// ─── API ──────────────────────────────────────────────────────────────────────
const API_BASE = 'https://kops.apiireit.com/gateway';

// ─── Server value formatters ──────────────────────────────────────────────────
// InputMask needs values already formatted with mask separators.
// Server may return raw digits (e.g. "12345678901" for CPF).
function fmtCPF(raw) {
  const d = (raw || '').replace(/\D/g, '').slice(0, 11);
  if (d.length < 4) return d;
  if (d.length < 7) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length < 10) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9, 11)}`;
}
function fmtPhone(raw) {
  const d = (raw || '').replace(/\D/g, '').slice(0, 11);
  if (d.length < 3) return d;
  if (d.length < 8) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length < 11) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7, 11)}`;
}

// ─── Persistence key ──────────────────────────────────────────────────────────
const DRAFT_KEY = 'ireit_mme_draft';

function saveDraft(data) {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(data));
  } catch (_) {
    console.log('Failed to save draft');
  }
}

function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function clearDraft() {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch (_) {
    console.log('Failed to clear draft');
  }
}

// ─── Animations ───────────────────────────────────────────────────────────────
const slideUp = keyframes`
  from { transform: translateY(28px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

const SlideUp = styled.div`
  animation: ${slideUp} 0.5s ease-out both;
  animation-delay: ${p => p.delay || '0s'};
`;

// ─── Colors ───────────────────────────────────────────────────────────────────
const C = {
  azul: '#06448e',
  azulD: '#04316a',
  laranja: '#FF6600',
  laranjaD: '#cc4400',
  roxo: '#7c53e3',
  bg: '#f4f6fa',
  text: '#1a1a2e',
  muted: '#8a94a6',
  white: '#ffffff',
  success: '#059669',
  warning: '#d97706',
  danger: '#e53935',
};

// ─── Steps config ─────────────────────────────────────────────────────────────
// id must match the field checks in checkExistingDriver()
const STEPS = [
  {id: 'auth', label: 'Conta', icon: '🔐'},
  {id: 'intencao', label: 'Intenção', icon: '🏍️'},
  {id: 'pessoal', label: 'Dados', icon: '👤'},
  {id: 'cnh', label: 'CNH', icon: '📄'},
  {id: 'veiculo', label: 'Veículo', icon: '🚗'},
  {id: 'localizacao', label: 'Localização', icon: '📍'},
  {id: 'banco', label: 'Pagamento', icon: '💳'},
  {id: 'nav', label: 'Navegação', icon: '🗺️'},
];

// Steps that belong to the linear progress bar (auth and done are outside)
const PROGRESS_STEPS = STEPS.filter(s => s.id !== 'auth' && s.id !== 'done');

// ─── Step indicator ───────────────────────────────────────────────────────────
const StepBar = ({current, sm}) => {
  const allIds = STEPS.map(s => s.id);
  const idx = allIds.indexOf(current);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: sm ? 3 : 6,
        marginBottom: 28,
        flexWrap: 'wrap',
      }}
    >
      {STEPS.map((s, i) => {
        const done = i < idx;
        const active = i === idx;
        return (
          <React.Fragment key={s.id}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
              }}
            >
              <div
                style={{
                  width: sm ? 30 : 38,
                  height: sm ? 30 : 38,
                  borderRadius: '50%',
                  background: done
                    ? C.success
                    : active
                    ? C.laranja
                    : 'rgba(255,255,255,0.2)',
                  border: `1px solid ${
                    done ? C.success : active ? C.laranja : '#06448e'
                  }`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: sm ? 12 : 16,
                  transition: 'all 0.3s ease',
                  flexShrink: 0,
                }}
              >
                {done ? '✓' : s.icon}
              </div>
              {!sm && (
                <span
                  style={{
                    fontSize: 10,
                    color: active ? '#06448e' : 'rgba(0,0,0,0.45)',
                    fontWeight: active ? 700 : 400,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {s.label}
                </span>
              )}
            </div>
            {i < STEPS.length - 1 && (
              <div
                style={{
                  width: sm ? 12 : 22,
                  height: 2,
                  marginBottom: sm ? 0 : 16,
                  background: i < idx ? C.success : 'rgba(255,255,255,0.15)',
                  borderRadius: 1,
                  flexShrink: 0,
                  transition: 'background 0.3s ease',
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ─── Progress strip (inside cards) ───────────────────────────────────────────
const ProgressStrip = ({current}) => {
  const ids = PROGRESS_STEPS.map(s => s.id);
  const idx = ids.indexOf(current);
  const pct = idx < 0 ? 0 : Math.round(((idx + 1) / ids.length) * 100);
  if (idx < 0) return null;
  return (
    <div style={{marginBottom: 20}}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 5,
        }}
      >
        <span style={{fontSize: 11, color: C.muted}}>
          Etapa {idx + 1} de {ids.length}
        </span>
        <span style={{fontSize: 11, color: C.laranja, fontWeight: 700}}>
          {pct}%
        </span>
      </div>
      <div
        style={{
          height: 4,
          background: '#f0f2f5',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            background: `linear-gradient(90deg, ${C.laranja}, ${C.roxo})`,
            borderRadius: 4,
            transition: 'width 0.4s ease',
          }}
        />
      </div>
    </div>
  );
};

// ─── Card shell ───────────────────────────────────────────────────────────────
const Card = ({children, sm}) => (
  <div
    style={{
      background: C.white,
      borderRadius: sm ? 16 : 24,
      padding: sm ? '24px 16px' : '36px 32px',
      maxWidth: 520,
      width: '100%',
      margin: '0 auto',
      boxShadow: '0 8px 40px rgba(0,0,0,0.10)',
      boxSizing: 'border-box',
    }}
  >
    {children}
  </div>
);

// ─── Section title ────────────────────────────────────────────────────────────
const STitle = ({children, sub}) => (
  <div style={{marginBottom: 22}}>
    <p style={{margin: 0, fontSize: 20, fontWeight: 800, color: C.text}}>
      {children}
    </p>
    {sub && (
      <p
        style={{
          margin: '4px 0 0',
          fontSize: 13,
          color: C.muted,
          lineHeight: 1.5,
        }}
      >
        {sub}
      </p>
    )}
  </div>
);

// ─── Nav buttons ─────────────────────────────────────────────────────────────
const NavRow = ({
  onBack,
  onNext,
  nextLabel = 'Continuar',
  nextDisabled,
  loading,
}) => (
  <div
    style={{
      display: 'flex',
      gap: 12,
      marginTop: 28,
      justifyContent: 'space-between',
      alignItems: 'center',
    }}
  >
    {onBack ? (
      <Button
        onClick={onBack}
        startIcon={<ArrowBack />}
        style={{textTransform: 'none', color: C.muted, fontSize: 14}}
      >
        Voltar
      </Button>
    ) : (
      <span />
    )}
    <Button
      onClick={onNext}
      disabled={nextDisabled || loading}
      endIcon={loading ? null : <ArrowForward />}
      sx={{
        bgcolor: C.laranja,
        color: '#fff',
        borderRadius: '12px',
        height: 48,
        px: 3,
        textTransform: 'none',
        fontWeight: 700,
        fontSize: 15,
        ':hover': {bgcolor: C.laranjaD},
        ':disabled': {bgcolor: '#e0e0e0', color: '#aaa'},
      }}
    >
      {loading ? (
        <CircularProgress size={20} style={{color: '#fff'}} />
      ) : (
        nextLabel
      )}
    </Button>
  </div>
);

// ─── Benefits for hero ────────────────────────────────────────────────────────
const benefits = [
  {icon: '📱', text: 'App iOS e Android'},
  {icon: '💰', text: 'Ganhos diários'},
  {icon: '🗺️', text: 'Rastreamento real'},
  {icon: '💬', text: 'Chat com passageiro'},
  {icon: '🎯', text: 'Missões e cupons'},
  {icon: '🏍️', text: 'Moto, carro ou delivery'},
];

// ══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════════════════════
const MotoristasCadastro = () => {
  const auth = getAuth();
  const sm = useMediaQuery({query: '(max-width: 750.98px)'});
  const vsm = useMediaQuery({query: '(max-width: 450px)'});

  // ── nav ──────────────────────────────────────────────────────────────────
  const [toggleBar, setToggleBar] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // ── step ─────────────────────────────────────────────────────────────────
  const [step, setStep] = useState('auth');

  // ── auth ─────────────────────────────────────────────────────────────────
  const [authUser, setAuthUser] = useState(null);
  const [authMode, setAuthMode] = useState('register');
  const [authName, setAuthName] = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [authPass, setAuthPass] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  // ── intencao ─────────────────────────────────────────────────────────────
  // type: 'Motos' | 'Carro' | 'Motos' (entregador moto) — aligns with ParceirosDadosmodels.type
  const [intencao, setIntencao] = useState(''); // 'mototaxista' | 'motorista' | 'entregador'
  const [vehicleType, setVehicleType] = useState(''); // 'Motos' | 'Carros' | etc.

  // ── dados pessoais ────────────────────────────────────────────────────────
  const [nome, setNome] = useState('');
  const [cpf, setCpf] = useState('');
  const [dob, setDob] = useState(''); // DD/MM/AAAA → sent as string
  const [gnro, setGnro] = useState(''); // M / F / NB
  const [celular, setCelular] = useState('');
  const [profileFile, setProfileFile] = useState(null); // File object

  // ── CNH ──────────────────────────────────────────────────────────────────
  const [cnhFile, setCnhFile] = useState(null); // File object (front photo)
  const [cnhCat, setCnhCat] = useState(''); // A | B | AB | etc.
  const [cnhExp, setCnhExp] = useState(''); // DD/MM/AAAA

  // ── vehicle ───────────────────────────────────────────────────────────────
  const [vcMarca, setVcMarca] = useState('');
  const [vcModelo, setVcModelo] = useState('');
  const [vcAno, setVcAno] = useState('');
  const [vcPlaca, setVcPlaca] = useState('');
  const [vcCor, setVcCor] = useState('');
  const [vcDocFile, setVcDocFile] = useState(null); // doc veiculo photo
  const [vcExp, setVcExp] = useState(''); // CRLV expiry DD/MM/AAAA
  const [vcCat, setVcCat] = useState(''); // 'Carro'|'Moto' — alias para vehicleType

  // ── localizacao + ponto ───────────────────────────────────────────────────
  const [userLat, setUserLat] = useState(null);
  const [userLng, setUserLng] = useState(null);
  const [pontosProx, setPontosProx] = useState([]);
  const [selectedPonto, setSelectedPonto] = useState(null); // PontoDadosModels
  const [skipPonto, setSkipPonto] = useState(false);

  // ── banco / pix ───────────────────────────────────────────────────────────
  const [banco, setBanco] = useState('');
  const [tconta, setTconta] = useState(''); // Corrente | Poupança
  const [agencia, setAgencia] = useState('');
  const [conta, setConta] = useState('');
  const [digito, setDigito] = useState('');
  const [pix, setPix] = useState('');

  // ── nav preference ────────────────────────────────────────────────────────
  const [navPref, setNavPref] = useState(''); // 'Google maps' | 'Waze'

  // ── loading / errors ──────────────────────────────────────────────────────
  const [globalLoading, setGlobalLoading] = useState(false);
  const [submitDone, setSubmitDone] = useState(false);

  // ── existing driver detected ──────────────────────────────────────────────
  const [driverData, setDriverData] = useState(null); // ParceirosDadosmodels

  // ─────────────────────────────────────────────────────────────────────────
  // PERSISTENCE — save draft on every meaningful state change
  // We skip File objects (not serialisable); only metadata is saved.
  // ─────────────────────────────────────────────────────────────────────────
  // Save draft whenever form fields change (authUser excluded — re-authed on return)
  useEffect(() => {
    if (!authUser) return; // nothing to persist until authenticated
    const draft = {
      step,
      uid: authUser.uid,
      authName,
      authEmail,
      intencao,
      vehicleType,
      nome,
      cpf,
      dob,
      gnro,
      celular,
      cnhCat,
      cnhExp,
      vcMarca,
      vcModelo,
      vcAno,
      vcPlaca,
      vcCor,
      userLat,
      userLng,
      selectedPonto: selectedPonto
        ? {
            _id: selectedPonto._id,
            nome: selectedPonto.nome,
            codigoPonto: selectedPonto.codigoPonto,
          }
        : null,
      skipPonto,
      banco,
      tconta,
      agencia,
      conta,
      digito,
      pix,
      navPref,
    };
    saveDraft(draft);
  }, [
    step,
    authUser,
    intencao,
    vehicleType,
    nome,
    cpf,
    dob,
    gnro,
    celular,
    cnhCat,
    cnhExp,
    vcMarca,
    vcModelo,
    vcAno,
    vcPlaca,
    vcCor,
    userLat,
    userLng,
    selectedPonto,
    skipPonto,
    banco,
    tconta,
    agencia,
    conta,
    digito,
    pix,
    navPref,
  ]);

  // ─────────────────────────────────────────────────────────────────────────
  // RESTORE DRAFT on mount
  // ─────────────────────────────────────────────────────────────────────────
  const draftRestoredRef = useRef(false);

  const restoreDraft = useCallback(draft => {
    if (!draft) return;
    if (draft.intencao) setIntencao(draft.intencao);
    if (draft.vehicleType) setVehicleType(draft.vehicleType);
    if (draft.nome) setNome(draft.nome);
    if (draft.cpf) setCpf(draft.cpf);
    if (draft.dob) setDob(draft.dob);
    if (draft.gnro) setGnro(draft.gnro);
    if (draft.celular) setCelular(draft.celular);
    if (draft.cnhCat) setCnhCat(draft.cnhCat);
    if (draft.cnhExp) setCnhExp(draft.cnhExp);
    if (draft.vcMarca) setVcMarca(draft.vcMarca);
    if (draft.vcModelo) setVcModelo(draft.vcModelo);
    if (draft.vcAno) setVcAno(draft.vcAno);
    if (draft.vcPlaca) setVcPlaca(draft.vcPlaca);
    if (draft.vcCor) setVcCor(draft.vcCor);
    if (draft.userLat) setUserLat(draft.userLat);
    if (draft.userLng) setUserLng(draft.userLng);
    if (draft.selectedPonto) setSelectedPonto(draft.selectedPonto);
    if (draft.skipPonto) setSkipPonto(draft.skipPonto);
    if (draft.banco) setBanco(draft.banco);
    if (draft.tconta) setTconta(draft.tconta);
    if (draft.agencia) setAgencia(draft.agencia);
    if (draft.conta) setConta(draft.conta);
    if (draft.digito) setDigito(draft.digito);
    if (draft.pix) setPix(draft.pix);
    if (draft.navPref) setNavPref(draft.navPref);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // CHECK EXISTING DRIVER — determines which step to resume
  // Logic mirrors the DadosConta observer in FirstFragment.kt
  // ─────────────────────────────────────────────────────────────────────────
  const checkExistingDriver = useCallback(
    async firebaseUser => {
      try {
        const token = await firebaseUser.getIdToken();
        const res = await fetch(
          `${API_BASE}/parceiros/get/user?userid=${firebaseUser.uid}`,
          {headers: {Authorization: `Bearer ${token}`}},
        );

        if (res.status === 404 || !res.ok) {
          // Brand-new driver — restore draft or start fresh
          const draft = loadDraft();
          if (
            draft &&
            draft.uid === firebaseUser.uid &&
            draft.step &&
            draft.step !== 'auth'
          ) {
            restoreDraft(draft);
            setStep(draft.step);
          } else {
            setStep('intencao');
          }
          return;
        }

        const data = await res.json();

        // Driver exists — store data and figure out which step is incomplete
        setDriverData(data);

        // Prefill fields — format CPF/phone so InputMask receives pre-separated values
        if (data.nome) setNome(data.nome);
        if (data.cpf) setCpf(fmtCPF(data.cpf));
        if (data.dob) setDob(data.dob);
        if (data.gnro) setGnro(data.gnro);
        if (data.Celular) setCelular(fmtPhone(data.Celular));
        if (data.type) setVehicleType(data.type);
        if (data.Banco) setBanco(data.Banco);
        if (data.TConta) setTconta(data.TConta);
        if (data.Agencia) setAgencia(data.Agencia);
        if (data.Conta) setConta(data.Conta);
        if (data.Digito) setDigito(data.Digito);
        if (data.pix) setPix(data.pix);
        if (data.nav) setNavPref(data.nav);
        // catcnhup is the pending update category; prefer it over approved catcnh
        if (data.catcnhup) setCnhCat(data.catcnhup);
        else if (data.catcnh) setCnhCat(data.catcnh);
        // cnhExp from cnhexpup timestamp (milliseconds)
        const rawExpMs = data.cnhexpup?.$numberLong ?? data.cnhexpup;
        const expMs =
          rawExpMs && rawExpMs !== 0 && rawExpMs !== '0'
            ? parseInt(rawExpMs)
            : 0;
        if (expMs > 0) {
          const dt = new Date(expMs);
          const dd = String(dt.getDate()).padStart(2, '0');
          const mm = String(dt.getMonth() + 1).padStart(2, '0');
          setCnhExp(`${dd}/${mm}/${dt.getFullYear()}`);
        }
        // Vehicle fields — Java model uses capitalized names (Veiculo, Marca, Placa)
        if (data.Marca) setVcMarca(data.Marca);
        if (data.Veiculo) setVcModelo(data.Veiculo);
        if (data.Placa) setVcPlaca(data.Placa);
        if (data.cor) setVcCor(data.cor);
        if (data.vcyear) setVcAno(String(data.vcyear));
        // Determine step — same priority order as FirstFragment observer
        const resumeStep = deriveStep(data);
        setStep(resumeStep);
      } catch {
        // Network error — restore draft if available
        const draft = loadDraft();
        if (draft && draft.uid === firebaseUser.uid && draft.step) {
          restoreDraft(draft);
          setStep(draft.step);
        } else {
          setStep('intencao');
        }
      }
    },
    [restoreDraft],
  );

  /**
   * Derive the earliest incomplete step from server ParceirosDadosmodels.
   * Returns the step id string.
   */
  const deriveStep = d => {
    // If pix is filled the user already completed vehicle + location + payment.
    // The vehicle may still be pending approval (type empty), but returning them
    // to intencao/veiculo would create a duplicate vehicle document.
    if (d.pix) {
      // Even with pix filled, profile photo or CNH photo might still be missing
      if (!d.Imagem) return 'pessoal';
      if (d.sttcnh === 0 && d.sttcnhup === 0 && !d.cnhup) return 'cnh';
      if (!d.nav) return 'nav';
      return 'done';
    }

    // 1. Intenção / tipo de veículo
    if (!d.type) return 'intencao';

    // 2. Dados pessoais
    if (!d.nome || !d.Imagem || !d.cpf || !d.dob) return 'pessoal';

    // 3. CNH
    if (d.sttcnh === 0 && d.sttcnhup === 0 && !d.cnhup) return 'cnh';

    // 4. Veículo — Java model serialises as capitalised (Veiculo, Marca, Placa)
    if (
      !(d.Veiculo || d.veiculo) ||
      !(d.Marca || d.marca) ||
      !(d.Placa || d.placa)
    )
      return 'veiculo';

    // 5. Localização / ponto
    if (d.pontoStatusVinculo === 0) return 'localizacao';

    // 6. Conta bancária
    if (!d.Banco || !d.Conta || !d.Agencia || !d.TConta) return 'banco';

    // 7. Navegação
    if (!d.nav) return 'nav';

    // All done
    return 'done';
  };

  /**
   * Given the step just completed and the existing driverData, return the next
   * step that still needs work — skipping steps whose data is already complete.
   * Only used when driverData is set (returning / existing driver).
   */
  const nextPendingStep = (afterStep, data) => {
    const order = [
      'intencao',
      'pessoal',
      'cnh',
      'veiculo',
      'localizacao',
      'banco',
      'nav',
    ];
    const afterIdx = order.indexOf(afterStep);
    const dVeiculo = data.Veiculo || data.veiculo;
    const dMarca = data.Marca || data.marca;
    const dPlaca = data.Placa || data.placa;
    const checks = [
      {
        step: 'cnh',
        needed: data.sttcnh === 0 && data.sttcnhup === 0 && !data.cnhup,
      },
      {step: 'veiculo', needed: !dVeiculo || !dMarca || !dPlaca},
      {step: 'localizacao', needed: data.pontoStatusVinculo === 0},
      {
        step: 'banco',
        needed: !data.Banco || !data.Conta || !data.Agencia || !data.TConta,
      },
      {step: 'nav', needed: !data.nav},
    ];
    for (const {step, needed} of checks) {
      if (order.indexOf(step) > afterIdx && needed) return step;
    }
    return 'done';
  };

  // ─────────────────────────────────────────────────────────────────────────
  // AUTH HANDLERS
  // ─────────────────────────────────────────────────────────────────────────
  const AUTH_ERROR_MAP = {
    'auth/email-already-in-use': 'Email já cadastrado. Faça login.',
    'auth/wrong-password': 'Senha incorreta.',
    'auth/user-not-found': 'Usuário não encontrado.',
    'auth/invalid-credential': 'Email ou senha incorretos.',
    'auth/too-many-requests': 'Muitas tentativas. Tente mais tarde.',
  };

  const afterAuth = useCallback(
    async user => {
      setAuthUser(user);
      if (user.displayName) setAuthName(user.displayName);
      if (user.email) setAuthEmail(user.email);
      await checkExistingDriver(user);
    },
    [checkExistingDriver],
  );

  const handleEmailAuth = async () => {
    setAuthError('');
    if (!authEmail.trim()) return setAuthError('Email obrigatório.');
    if (!authPass) return setAuthError('Senha obrigatória.');
    if (authMode === 'register') {
      if (!authName.trim()) return setAuthError('Informe seu nome.');
      if (authPass.length < 6)
        return setAuthError('Senha: mínimo 6 caracteres.');
    }
    setAuthLoading(true);
    try {
      let cred;
      if (authMode === 'register') {
        cred = await createUserWithEmailAndPassword(
          auth,
          authEmail.trim(),
          authPass,
        );
        await updateProfile(cred.user, {displayName: authName.trim()});
      } else {
        cred = await signInWithEmailAndPassword(
          auth,
          authEmail.trim(),
          authPass,
        );
      }
      await afterAuth(cred.user);
    } catch (err) {
      setAuthError(AUTH_ERROR_MAP[err.code] || 'Erro ao autenticar.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleGoogle = async () => {
    setAuthError('');
    setAuthLoading(true);
    try {
      const res = await signInWithPopup(auth, new GoogleAuthProvider());
      await afterAuth(res.user);
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user')
        setAuthError('Erro ao entrar com Google.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleApple = async () => {
    setAuthError('');
    setAuthLoading(true);
    try {
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      const res = await signInWithPopup(auth, provider);
      await afterAuth(res.user);
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user')
        setAuthError('Erro ao entrar com Apple.');
    } finally {
      setAuthLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // HELPERS
  // ─────────────────────────────────────────────────────────────────────────
  const getToken = useCallback(async () => {
    if (!authUser) throw new Error('not authenticated');
    return authUser.getIdToken();
  }, [authUser]);

  const goTo = stepId => {
    setStep(stepId);
  };

  // ── Status pre-configuration ──────────────────────────────────────────────
  // GET the driver status doc (creates it if missing), merges updates, PUT back.
  // Fire-and-forget: errors are silent so they never block the registration flow.
  // GET status doc (creates if missing) then PUT back with merged updates.
  const syncStatusUpdate = useCallback(
    async updates => {
      if (!authUser) return;
      try {
        const token = await getToken();
        const getRes = await fetch(
          `${API_BASE}/parceiros/status/parceiro?idmotorista=${authUser.uid}`,
          {headers: {Authorization: `Bearer ${token}`}},
        );
        if (!getRes.ok) return;
        const doc = await getRes.json();
        await fetch(`${API_BASE}/parceiros/status/update/${doc._id}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({...doc, ...updates}),
        });
      } catch (e) {
        console.warn('[status] sync failed:', e);
      }
    },
    [authUser, getToken],
  );

  // Dedicated location endpoint — matches mobile PUT /parceiros/status/location
  const syncStatusLocation = useCallback(
    async (lat, lng) => {
      if (!authUser) return;
      try {
        const token = await getToken();
        await fetch(
          `${API_BASE}/parceiros/status/location?user=${authUser.uid}&lat=${lat}&lng=${lng}&rot=0`,
          {
            method: 'PUT',
            headers: {Authorization: `Bearer ${token}`},
          },
        );
      } catch (e) {
        console.warn('[status] location sync failed:', e);
      }
    },
    [authUser, getToken],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // EFFECTS
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    signOut(auth).catch(() => {});
    // If a draft exists, pre-fill email so the user recognises it
    const draft = loadDraft();
    if (draft?.authEmail) setAuthEmail(draft.authEmail);
  }, []);

  useEffect(() => {
    window.scrollTo({top: 0, behavior: 'smooth'});
  }, [step]);

  useEffect(() => {
    const onScroll = () => setIsVisible(window.pageYOffset > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // ──────────────────────────────────────────────────────────────────────────
  // STEP RENDERERS
  // ──────────────────────────────────────────────────────────────────────────

  // ── STEP 1: Auth ──────────────────────────────────────────────────────────
  const renderAuth = () => (
    <SlideUp delay="0.05s">
      <Card sm={sm}>
        {/* Resume banner if draft exists */}
        {loadDraft() && (
          <div
            style={{
              background: 'rgba(6,68,142,0.06)',
              border: `1px solid ${C.azul}30`,
              borderRadius: 10,
              padding: '10px 14px',
              marginBottom: 20,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <span style={{fontSize: 18}}>💾</span>
            <p
              style={{margin: 0, fontSize: 13, color: C.azul, fontWeight: 600}}
            >
              Você tem um cadastro em andamento. Entre na mesma conta para
              continuar de onde parou.
            </p>
          </div>
        )}

        {/* Toggle register / login */}
        <div
          style={{
            display: 'flex',
            borderRadius: 12,
            background: '#f4f6fa',
            padding: 4,
            marginBottom: 24,
          }}
        >
          {['register', 'login'].map(m => (
            <button
              key={m}
              onClick={() => {
                setAuthMode(m);
                setAuthError('');
              }}
              style={{
                flex: 1,
                padding: '9px 0',
                border: 'none',
                borderRadius: 9,
                background: authMode === m ? '#fff' : 'transparent',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: 14,
                color: authMode === m ? C.azul : C.muted,
                boxShadow:
                  authMode === m ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.2s',
                fontFamily: "'Outfit','Segoe UI',sans-serif",
              }}
            >
              {m === 'register' ? 'Criar conta' : 'Já tenho conta'}
            </button>
          ))}
        </div>

        <STitle sub="Crie uma conta de motorista para começar o cadastro.">
          {authMode === 'register' ? 'Crie sua conta' : 'Entre na sua conta'}
        </STitle>

        {authMode === 'register' && (
          <TextField
            label="Seu nome completo"
            variant="outlined"
            size="small"
            fullWidth
            style={{marginBottom: 14}}
            value={authName}
            disabled={authLoading}
            onChange={e => setAuthName(e.target.value)}
            autoComplete="name"
          />
        )}

        <TextField
          label="Email"
          type="email"
          variant="outlined"
          size="small"
          fullWidth
          style={{marginBottom: 14}}
          value={authEmail}
          disabled={authLoading}
          onChange={e => setAuthEmail(e.target.value)}
          autoComplete="email"
        />

        <TextField
          label="Senha"
          variant="outlined"
          size="small"
          fullWidth
          style={{marginBottom: 8}}
          type={showPass ? 'text' : 'password'}
          value={authPass}
          disabled={authLoading}
          onChange={e => setAuthPass(e.target.value)}
          autoComplete={
            authMode === 'register' ? 'new-password' : 'current-password'
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setShowPass(v => !v)}
                  edge="end"
                >
                  {showPass ? (
                    <VisibilityOff fontSize="small" />
                  ) : (
                    <Visibility fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') handleEmailAuth();
          }}
        />

        {authError && (
          <p
            style={{
              color: C.danger,
              fontSize: 13,
              textAlign: 'center',
              marginBottom: 8,
              marginTop: 4,
            }}
          >
            {authError}
          </p>
        )}

        <Button
          fullWidth
          variant="contained"
          onClick={handleEmailAuth}
          disabled={authLoading}
          sx={{
            bgcolor: C.azul,
            ':hover': {bgcolor: C.azulD},
            borderRadius: '12px',
            height: 50,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 16,
            mt: '8px',
          }}
        >
          {authLoading ? (
            <CircularProgress size={22} style={{color: '#fff'}} />
          ) : authMode === 'register' ? (
            'Criar conta e continuar'
          ) : (
            'Entrar e continuar'
          )}
        </Button>

        <Divider style={{margin: '18px 0'}}>
          <span style={{fontSize: 12, color: C.muted, padding: '0 8px'}}>
            ou continuar com
          </span>
        </Divider>

        {/* Google */}
        <button
          onClick={handleGoogle}
          disabled={authLoading}
          style={{
            width: '100%',
            height: 48,
            borderRadius: 12,
            border: '1px solid #e0e0e0',
            background: '#fff',
            color: '#3c4043',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: 14,
            fontFamily: "'Outfit','Segoe UI',sans-serif",
            marginBottom: 10,
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = '#f8f9fa')}
          onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Continuar com Google
        </button>

        {/* Apple */}
        <button
          onClick={handleApple}
          disabled={authLoading}
          style={{
            width: '100%',
            height: 48,
            borderRadius: 12,
            border: 'none',
            background: '#000',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: 14,
            fontFamily: "'Outfit','Segoe UI',sans-serif",
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = '#222')}
          onMouseLeave={e => (e.currentTarget.style.background = '#000')}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="#fff">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
          </svg>
          Continuar com Apple
        </button>
      </Card>
    </SlideUp>
  );

  // ── STEPS 2-8: Placeholders — to be filled step-by-step ──────────────────
  // Each placeholder shows what data will be collected in that step.
  const renderPlaceholder = (stepId, title, fields) => (
    <SlideUp delay="0.05s">
      <Card sm={sm}>
        <ProgressStrip current={stepId} />
        <STitle sub={`Etapa: ${stepId}`}>{title}</STitle>
        <div
          style={{
            background: '#f8f9fb',
            borderRadius: 12,
            padding: 16,
            border: '1px dashed #d0d8ee',
            marginBottom: 16,
          }}
        >
          <p
            style={{
              margin: '0 0 8px',
              fontSize: 11,
              fontWeight: 700,
              color: C.muted,
              textTransform: 'uppercase',
            }}
          >
            Campos desta etapa
          </p>
          {fields.map(f => (
            <div
              key={f}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '5px 0',
                borderBottom: '1px solid #eef0f4',
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: C.laranja,
                  flexShrink: 0,
                }}
              />
              <span style={{fontSize: 13, color: C.text}}>{f}</span>
            </div>
          ))}
        </div>
        <p
          style={{
            fontSize: 12,
            color: C.muted,
            textAlign: 'center',
            fontStyle: 'italic',
          }}
        >
          Em construção — este step será implementado em breve.
        </p>
        <NavRow
          onBack={() => {
            const ids = STEPS.map(s => s.id);
            const i = ids.indexOf(stepId);
            if (i > 0) goTo(ids[i - 1]);
          }}
          onNext={() => {
            const ids = STEPS.map(s => s.id);
            const i = ids.indexOf(stepId);
            if (i < ids.length - 1) goTo(ids[i + 1]);
          }}
        />
      </Card>
    </SlideUp>
  );

  // ── STEP DONE ─────────────────────────────────────────────────────────────
  const renderDone = () => {
    clearDraft(); // wipe persisted data after successful completion
    return (
      <SlideUp delay="0.05s">
        <Card sm={sm}>
          <div style={{textAlign: 'center', padding: '8px 0 16px'}}>
            <CheckCircleOutline
              style={{fontSize: 72, color: C.success, marginBottom: 12}}
            />
            <p
              style={{margin: 0, fontSize: 22, fontWeight: 800, color: C.text}}
            >
              Cadastro enviado!
            </p>
            <p
              style={{
                margin: '8px 0 24px',
                fontSize: 14,
                color: C.muted,
                lineHeight: 1.6,
              }}
            >
              Nossa equipe irá analisar seu cadastro. Enquanto isso, baixe o app
              para ficar pronto assim que for aprovado.
            </p>

            {/* Download CTAs */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                marginBottom: 24,
              }}
            >
              <a
                href="https://apps.apple.com/br/app/ire-it-mme/id6759395810"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  background: '#000',
                  color: '#fff',
                  borderRadius: 14,
                  padding: '14px 20px',
                  textDecoration: 'none',
                  fontWeight: 700,
                  fontSize: 15,
                  fontFamily: "'Outfit','Segoe UI',sans-serif",
                }}
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="#fff">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
                Baixar para iPhone (App Store)
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.ireit.ireitmotoristasemototaxistas&hl=pt"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  background: C.azul,
                  color: '#fff',
                  borderRadius: 14,
                  padding: '14px 20px',
                  textDecoration: 'none',
                  fontWeight: 700,
                  fontSize: 15,
                  fontFamily: "'Outfit','Segoe UI',sans-serif",
                }}
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="#fff">
                  <path d="M3.18 23.76c.3.16.63.24.97.24.46 0 .91-.13 1.31-.38L17.12 17l-3.37-3.37L3.18 23.76zm-1.15-20.5C1.74 3.63 1.5 4.08 1.5 4.6v14.8c0 .52.24.97.53 1.34l.07.07 8.29-8.29v-.2L2.1 3.19l-.07.07zM20.32 10.5l-2.38-1.38-3.78 3.78 3.78 3.78 2.4-1.4c.68-.4.68-1.38 0-1.78l-2.4-1.4 2.38-1.6zm-17.14 9.84L14.84 12 11.47 8.63 3.18 2.24c-.3-.16-.66-.24-1-.24-.34 0-.68.08-.98.24L3.18 20.34z" />
                </svg>
                Baixar para Android (Google Play)
              </a>
            </div>

            <div
              style={{
                background: '#f4f6fa',
                borderRadius: 12,
                padding: '14px 20px',
                textAlign: 'left',
              }}
            >
              <p
                style={{
                  margin: '0 0 8px',
                  fontSize: 11,
                  fontWeight: 700,
                  color: C.muted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}
              >
                Próximos passos
              </p>
              {[
                'Análise do cadastro em até 48h',
                'Email de confirmação quando aprovado',
                'Abra o app e conecte-se para receber corridas',
              ].map((t, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    gap: 10,
                    marginBottom: 6,
                    alignItems: 'flex-start',
                  }}
                >
                  <span
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: C.azul,
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {i + 1}
                  </span>
                  <span style={{fontSize: 13, color: C.text}}>{t}</span>
                </div>
              ))}
            </div>

            <Button
              component={Link}
              to="/"
              sx={{
                mt: 3,
                bgcolor: C.laranja,
                color: '#fff',
                borderRadius: '12px',
                height: 48,
                px: 4,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 15,
                ':hover': {bgcolor: C.laranjaD},
              }}
            >
              Voltar ao início
            </Button>
          </div>
        </Card>
      </SlideUp>
    );
  };

  // ──────────────────────────────────────────────────────────────────────────
  // PAGE RENDER
  // ──────────────────────────────────────────────────────────────────────────
  const showStepBar = !['auth', 'done'].includes(step);

  return (
    <div
      style={{
        overflowX: 'hidden',
        width: '100%',
        fontFamily: "'Outfit','Segoe UI',sans-serif",
      }}
    >
      {/* Scroll-to-top FAB */}
      {isVisible && (
        <Fab
          onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
          style={{position: 'fixed', bottom: 20, left: 20, zIndex: 2}}
          size="small"
          color="primary"
        >
          <ArrowUpwardIcon />
        </Fab>
      )}

      {/* Navbar */}
      <div
        style={{
          background: C.azul,
          height: 60,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'fixed',
          zIndex: 9999,
          top: 0,
          left: 0,
          width: '100%',
        }}
      >
        <Button
          component={Link}
          to="/"
          style={{
            width: 95,
            height: 47,
            marginLeft: 12,
            backgroundImage: `url(${logo})`,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
          }}
        />
        {sm || vsm ? (
          <MenuIcon
            style={{color: '#fff', marginRight: 15}}
            onClick={() => setToggleBar(true)}
          />
        ) : (
          <Tab />
        )}
      </div>
      {(sm || vsm) && (
        <Drawer
          anchor="top"
          open={toggleBar}
          onClose={() => setToggleBar(false)}
        >
          <Tab setToggleBar={setToggleBar} />
        </Drawer>
      )}

      {/* HERO */}
      <div
        style={{
          background: `linear-gradient(145deg, ${C.azul} 0%, ${C.azul} 35%, ${C.roxo} 75%, ${C.roxo} 100%)`,
          paddingTop: sm ? 80 : 100,
          paddingBottom: sm ? 40 : 60,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative blobs */}
        <div
          style={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 350,
            height: 350,
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(255,102,0,0.1) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: -60,
            left: -60,
            width: 260,
            height: 260,
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(124,83,227,0.12) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />

        <div
          style={{
            maxWidth: 620,
            margin: '0 auto',
            padding: sm ? '0 16px' : '0 24px',
            textAlign: 'center',
          }}
        >
          <SlideUp delay="0.05s">
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 20,
                background: 'rgba(255,102,0,0.18)',
                border: '1px solid rgba(255,102,0,0.4)',
                borderRadius: 20,
                padding: '5px 16px',
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: C.laranja,
                  display: 'inline-block',
                }}
              />
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: C.laranja,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                Seja um parceiro Irê it
              </span>
            </div>
          </SlideUp>

          <SlideUp delay="0.1s">
            <h1
              style={{
                color: '#fff',
                fontSize: sm ? 28 : 42,
                fontWeight: 800,
                margin: '0 0 12px',
                lineHeight: 1.1,
                letterSpacing: -0.5,
              }}
            >
              Cadastro de <span style={{color: C.laranja}}>Motorista,</span>
              <br />
              Mototaxista e Entregador
            </h1>
          </SlideUp>

          <SlideUp delay="0.15s">
            <p
              style={{
                color: 'rgba(255,255,255,0.65)',
                fontSize: sm ? 14 : 17,
                margin: '0 auto 24px',
                maxWidth: 460,
                lineHeight: 1.55,
              }}
            >
              Faça seu cadastro uma única vez pelo site. Mais rápido, mais claro
              — sem travar o celular.
            </p>
          </SlideUp>

          <SlideUp delay="0.2s">
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 7,
                justifyContent: 'center',
              }}
            >
              {benefits.map(b => (
                <span
                  key={b.text}
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: 20,
                    padding: '5px 11px',
                    fontSize: 12,
                    color: 'rgba(255,255,255,0.85)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                >
                  {b.icon} {b.text}
                </span>
              ))}
            </div>
          </SlideUp>

          <SlideUp delay="0.25s">
            <div
              style={{
                marginTop: 28,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: 12,
                  color: 'rgba(255,255,255,0.45)',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}
              >
                Prefere baixar o app?
              </p>
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                }}
              >
                <a
                  href="https://play.google.com/store/apps/details?id=com.ireit.ireitmotoristasemototaxistas&hl=pt"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 18px',
                    borderRadius: 10,
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    color: '#fff',
                    textDecoration: 'none',
                    fontSize: 13,
                    fontWeight: 600,
                    backdropFilter: 'blur(6px)',
                    transition: 'background 0.15s',
                  }}
                >
                  <Android sx={{fontSize: 18}} /> Play Store
                </a>
                <a
                  href="https://apps.apple.com/br/app/ire-it-mme/id6759395810"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 18px',
                    borderRadius: 10,
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    color: '#fff',
                    textDecoration: 'none',
                    fontSize: 13,
                    fontWeight: 600,
                    backdropFilter: 'blur(6px)',
                    transition: 'background 0.15s',
                  }}
                >
                  <Apple sx={{fontSize: 18}} /> App Store
                </a>
              </div>
            </div>
          </SlideUp>
        </div>
      </div>

      {/* FORM AREA */}
      <div
        style={{
          background: C.bg,
          padding: sm ? '32px 12px 60px' : '48px 24px 80px',
        }}
      >
        {showStepBar && (
          <div style={{maxWidth: 620, margin: '0 auto'}}>
            <StepBar current={step} sm={sm} />
          </div>
        )}

        <div style={{maxWidth: 520, margin: '0 auto'}}>
          {step === 'auth' && renderAuth()}

          {step === 'intencao' && (
            <SlideUp delay="0.05s">
              <Card sm={sm}>
                <ProgressStrip current="intencao" />
                <StepIntencao
                  sm={sm}
                  intencao={intencao}
                  setIntencao={setIntencao}
                  setVehicleType={setVehicleType}
                  onNext={() => {
                    // Pre-configure status: bag flag + default maq=false
                    const bag = intencao === 'entregador';
                    syncStatusUpdate({bag, maq: false}).catch(() => {});

                    if (driverData) {
                      const d = driverData;
                      if (!d.nome || !d.Imagem || !d.cpf || !d.dob)
                        goTo('pessoal');
                      else if (d.sttcnh === 0 && d.sttcnhup === 0 && !d.cnhup)
                        goTo('cnh');
                      else if (!d.veiculo || !d.marca || !d.placa)
                        goTo('veiculo');
                      else if (d.pontoStatusVinculo === 0) goTo('localizacao');
                      else if (!d.Banco || !d.Conta || !d.Agencia || !d.TConta)
                        goTo('banco');
                      else if (!d.nav) goTo('nav');
                      else goTo('done');
                    } else {
                      goTo('pessoal');
                    }
                  }}
                  onBack={null}
                />
              </Card>
            </SlideUp>
          )}

          {step === 'pessoal' && (
            <SlideUp delay="0.05s">
              <Card sm={sm}>
                <ProgressStrip current="pessoal" />
                <StepPessoal
                  authUser={authUser}
                  getToken={getToken}
                  sm={sm}
                  existingPhotoUrl={driverData?.Imagem || null}
                  driverDocId={driverData?._id || null}
                  nome={nome}
                  setNome={setNome}
                  cpf={cpf}
                  setCpf={setCpf}
                  dob={dob}
                  setDob={setDob}
                  gnro={gnro}
                  setGnro={setGnro}
                  celular={celular}
                  setCelular={setCelular}
                  profileFile={profileFile}
                  setProfileFile={setProfileFile}
                  onNext={(docId, specificStep) => {
                    if (specificStep) {
                      goTo(specificStep);
                    } else {
                      setDriverData(prev =>
                        prev ? {...prev, _id: docId} : {_id: docId},
                      );
                      if (driverData) {
                        goTo(nextPendingStep('pessoal', driverData));
                      } else {
                        goTo('cnh');
                      }
                    }
                  }}
                  onBack={() => goTo('intencao')}
                />
              </Card>
            </SlideUp>
          )}

          {step === 'cnh' && (
            <SlideUp delay="0.05s">
              <Card sm={sm}>
                <ProgressStrip current="cnh" />
                <StepCnh
                  authUser={authUser}
                  getToken={getToken}
                  sm={sm}
                  driverData={driverData}
                  cnhFile={cnhFile}
                  setCnhFile={setCnhFile}
                  cnhCat={cnhCat}
                  setCnhCat={setCnhCat}
                  cnhExp={cnhExp}
                  setCnhExp={setCnhExp}
                  onNext={() => {
                    if (driverData) {
                      goTo(nextPendingStep('cnh', driverData));
                    } else {
                      goTo('veiculo');
                    }
                  }}
                  onBack={() => goTo('pessoal')}
                />
              </Card>
            </SlideUp>
          )}

          {step === 'veiculo' && (
            <SlideUp delay="0.05s">
              <Card sm={sm}>
                <ProgressStrip current="veiculo" />
                <StepVeiculo
                  authUser={authUser}
                  getToken={getToken}
                  sm={sm}
                  intencao={intencao}
                  existingDocUrl={driverData?.Docveiculo || null}
                  vcMarca={vcMarca}
                  setVcMarca={setVcMarca}
                  vcModelo={vcModelo}
                  setVcModelo={setVcModelo}
                  vcAno={vcAno}
                  setVcAno={setVcAno}
                  vcPlaca={vcPlaca}
                  setVcPlaca={setVcPlaca}
                  vcCor={vcCor}
                  setVcCor={setVcCor}
                  vcCat={vehicleType}
                  setVcCat={setVehicleType}
                  vcExp={vcExp}
                  setVcExp={setVcExp}
                  vcDocFile={vcDocFile}
                  setVcDocFile={setVcDocFile}
                  onNext={() => goTo('localizacao')}
                  onBack={() => goTo('cnh')}
                />
              </Card>
            </SlideUp>
          )}

          {step === 'localizacao' && (
            <SlideUp delay="0.05s">
              <Card sm={sm}>
                <ProgressStrip current="localizacao" />
                <StepLocation
                  authUser={authUser}
                  getToken={getToken}
                  userLat={userLat}
                  setUserLat={setUserLat}
                  userLng={userLng}
                  setUserLng={setUserLng}
                  pontosProx={pontosProx}
                  setPontosProx={setPontosProx}
                  selectedPonto={selectedPonto}
                  setSelectedPonto={setSelectedPonto}
                  skipPonto={skipPonto}
                  setSkipPonto={setSkipPonto}
                  onNext={() => {
                    if (userLat && userLng) {
                      syncStatusLocation(userLat, userLng).catch(() => {});
                    }
                    goTo('banco');
                  }}
                  onBack={() => goTo('veiculo')}
                />
              </Card>
            </SlideUp>
          )}

          {step === 'banco' && (
            <SlideUp delay="0.05s">
              <Card sm={sm}>
                <ProgressStrip current="banco" />
                <StepPagamento
                  authUser={authUser}
                  getToken={getToken}
                  banco={banco}
                  setBanco={setBanco}
                  tconta={tconta}
                  setTconta={setTconta}
                  agencia={agencia}
                  setAgencia={setAgencia}
                  conta={conta}
                  setConta={setConta}
                  digito={digito}
                  setDigito={setDigito}
                  pix={pix}
                  setPix={setPix}
                  onNext={() => goTo('nav')}
                  onBack={() => goTo('localizacao')}
                  sm={sm}
                />
              </Card>
            </SlideUp>
          )}

          {step === 'nav' && (
            <SlideUp delay="0.05s">
              <Card sm={sm}>
                <ProgressStrip current="nav" />
                <StepNav
                  authUser={authUser}
                  getToken={getToken}
                  navPref={navPref}
                  setNavPref={setNavPref}
                  onNext={() => goTo('done')}
                  onBack={() => goTo('banco')}
                  sm={sm}
                />
              </Card>
            </SlideUp>
          )}

          {step === 'done' && renderDone()}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MotoristasCadastro;
